import React, { useState } from 'react';
import { Sidebar } from '../../components/Sidebar';
import { Header } from '../../components/Header';
import { StepIndicator } from '../../components/StepIndicator';
import { DownloadIcon, ImageIcon, VideoIcon, CheckCircleIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const STEPS = ['Select Product', 'Background', 'Greetings', 'Store Info', 'Generate', 'Output Format'];
export function ChooseOutputFormat() {
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState<'image' | 'video' | 'both' | null>(null);
  const [selectedFormats, setSelectedFormats] = useState<string[]>(['1:1']);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const toggleFormat = (format: string) => {
    setSelectedFormats(prev => prev.includes(format) ? prev.filter(f => f !== format) : [...prev, format]);
  };
  const handleGenerate = () => {
    if (selectedOption === 'video' || selectedOption === 'both') {
      setIsGeneratingVideo(true);
      const interval = setInterval(() => {
        setVideoProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setIsGeneratingVideo(false);
            setIsComplete(true);
            return 100;
          }
          return prev + 10;
        });
      }, 200);
    } else {
      setIsComplete(true);
    }
  };
  return <div className="flex w-full min-h-screen bg-gray-50" data-id="element-1160">
      <Sidebar data-id="element-1161" />
      <main className="flex-1" data-id="element-1162">
        <Header data-id="element-1163" />
        <StepIndicator currentStep={6} steps={STEPS} data-id="element-1164" />
        <div className="px-6 py-8" data-id="element-1165">
          <div className="max-w-5xl mx-auto" data-id="element-1166">
            <h1 className="text-2xl font-semibold text-gray-800 mb-2" data-id="element-1167">
              Step 6: Choose Output Format
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-1168">
              Download as image, generate AI video, or get both
            </p>

            {!isComplete ? <div className="space-y-6" data-id="element-1169">
                <div className="grid grid-cols-3 gap-6" data-id="element-1170">
                  {/* Image Option */}
                  <button onClick={() => setSelectedOption('image')} className={`bg-white rounded-lg border-2 p-6 text-left transition-all ${selectedOption === 'image' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1171">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4" data-id="element-1172">
                      <ImageIcon className="text-blue-500" size={24} data-id="element-1173" />
                    </div>
                    <h3 className="font-semibold text-gray-800 mb-2" data-id="element-1174">
                      Download Image
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-1175">
                      Get static creative in multiple formats
                    </p>
                    <div className="flex items-center justify-between" data-id="element-1176">
                      <span className="text-xs text-gray-500" data-id="element-1177">⏱️ Instant</span>
                      {selectedOption === 'image' && <CheckCircleIcon className="text-blue-500" size={20} data-id="element-1178" />}
                    </div>
                  </button>

                  {/* Video Option */}
                  <button onClick={() => setSelectedOption('video')} className={`bg-white rounded-lg border-2 p-6 text-left transition-all ${selectedOption === 'video' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1179">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4" data-id="element-1180">
                      <VideoIcon className="text-purple-500" size={24} data-id="element-1181" />
                    </div>
                    <h3 className="font-semibold text-gray-800 mb-2" data-id="element-1182">
                      Generate AI Video
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-1183">
                      8-second video with animations
                    </p>
                    <div className="flex items-center justify-between" data-id="element-1184">
                      <span className="text-xs text-gray-500" data-id="element-1185">⏱️ 2 mins</span>
                      {selectedOption === 'video' && <CheckCircleIcon className="text-purple-500" size={20} data-id="element-1186" />}
                    </div>
                  </button>

                  {/* Both Option */}
                  <button onClick={() => setSelectedOption('both')} className={`bg-white rounded-lg border-2 p-6 text-left transition-all ${selectedOption === 'both' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1187">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-1188">
                      <DownloadIcon className="text-green-500" size={24} data-id="element-1189" />
                    </div>
                    <h3 className="font-semibold text-gray-800 mb-2" data-id="element-1190">
                      Download Both
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-1191">
                      Get image + video versions
                    </p>
                    <div className="flex items-center justify-between" data-id="element-1192">
                      <span className="text-xs text-gray-500" data-id="element-1193">⏱️ 2 mins</span>
                      {selectedOption === 'both' && <CheckCircleIcon className="text-green-500" size={20} data-id="element-1194" />}
                    </div>
                  </button>
                </div>

                {selectedOption && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1195">
                    <h3 className="font-medium text-gray-800 mb-4" data-id="element-1196">
                      Select Output Formats
                    </h3>
                    <div className="grid grid-cols-3 gap-4" data-id="element-1197">
                      {[{
                  id: '1:1',
                  name: 'Square (1:1)',
                  desc: 'Facebook Feed, Instagram Post'
                }, {
                  id: '9:16',
                  name: 'Portrait (9:16)',
                  desc: 'Instagram Story, YouTube Shorts'
                }, {
                  id: '16:9',
                  name: 'Landscape (16:9)',
                  desc: 'Google Display, YouTube'
                }].map(format => <button key={format.id} onClick={() => toggleFormat(format.id)} className={`p-4 rounded-lg border-2 transition-all text-left ${selectedFormats.includes(format.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1198">
                          <div className="flex items-center justify-between mb-2" data-id="element-1199">
                            <span className="font-medium text-gray-800 text-sm" data-id="element-1200">
                              {format.name}
                            </span>
                            {selectedFormats.includes(format.id) && <CheckCircleIcon className="text-blue-500 flex-shrink-0" size={18} data-id="element-1201" />}
                          </div>
                          <p className="text-xs text-gray-500" data-id="element-1202">{format.desc}</p>
                        </button>)}
                    </div>
                  </div>}

                {(selectedOption === 'video' || selectedOption === 'both') && <div className="bg-purple-50 border border-purple-200 rounded-lg p-6" data-id="element-1203">
                    <h4 className="font-medium text-purple-900 mb-3" data-id="element-1204">
                      AI Video Features
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm text-purple-800" data-id="element-1205">
                      <div data-id="element-1206">
                        <p className="font-medium mb-1" data-id="element-1207">Product Animation</p>
                        <p className="text-xs" data-id="element-1208">
                          3D rotation, movement, zoom effects
                        </p>
                      </div>
                      <div data-id="element-1209">
                        <p className="font-medium mb-1" data-id="element-1210">Background Motion</p>
                        <p className="text-xs" data-id="element-1211">
                          Parallax scrolling, cinematic panning
                        </p>
                      </div>
                      <div data-id="element-1212">
                        <p className="font-medium mb-1" data-id="element-1213">Text Animations</p>
                        <p className="text-xs" data-id="element-1214">Fade in/out at 2-3s and 6-7s</p>
                      </div>
                      <div data-id="element-1215">
                        <p className="font-medium mb-1" data-id="element-1216">Duration</p>
                        <p className="text-xs" data-id="element-1217">8 seconds (social optimized)</p>
                      </div>
                    </div>
                  </div>}

                {isGeneratingVideo && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1218">
                    <h3 className="font-medium text-gray-800 mb-4" data-id="element-1219">
                      Generating AI Video...
                    </h3>
                    <div className="mb-4" data-id="element-1220">
                      <div className="flex justify-between text-sm text-gray-600 mb-2" data-id="element-1221">
                        <span data-id="element-1222">Progress</span>
                        <span data-id="element-1223">{videoProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3" data-id="element-1224">
                        <div className="bg-purple-500 h-3 rounded-full transition-all duration-300" style={{
                    width: `${videoProgress}%`
                  }} data-id="element-1225"></div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-1226">
                      Creating professional video with animations...
                    </p>
                  </div>}

                <div className="flex justify-between" data-id="element-1227">
                  <button onClick={() => navigate('/dealer/ai-generator/step-5')} className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1228">
                    Back
                  </button>
                  <button onClick={handleGenerate} disabled={!selectedOption || selectedFormats.length === 0 || isGeneratingVideo} className="px-8 py-3 bg-green-500 text-white rounded-md font-medium hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors" data-id="element-1229">
                    {isGeneratingVideo ? 'Generating...' : 'Generate & Download'}
                  </button>
                </div>
              </div> : <div className="space-y-6" data-id="element-1230">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6" data-id="element-1231">
                  <div className="flex items-center" data-id="element-1232">
                    <CheckCircleIcon className="text-green-500 mr-3 flex-shrink-0" size={32} data-id="element-1233" />
                    <div data-id="element-1234">
                      <h2 className="text-xl font-semibold text-green-900" data-id="element-1235">
                        Creatives Ready!
                      </h2>
                      <p className="text-sm text-green-700" data-id="element-1236">
                        Your{' '}
                        {selectedOption === 'both' ? 'image and video' : selectedOption}{' '}
                        creatives are ready to download
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1237">
                  <h3 className="font-medium text-gray-800 mb-4" data-id="element-1238">
                    Download Files
                  </h3>
                  <div className="grid grid-cols-3 gap-4" data-id="element-1239">
                    {selectedFormats.map(format => <div key={format} className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center" data-id="element-1240">
                        <DownloadIcon className="mx-auto text-gray-600 mb-2" size={24} data-id="element-1241" />
                        <p className="font-semibold text-gray-800" data-id="element-1242">{format}</p>
                        <p className="text-xs text-gray-600 mt-1" data-id="element-1243">
                          {selectedOption === 'both' ? 'Image + Video' : selectedOption}
                        </p>
                      </div>)}
                  </div>
                </div>

                <div className="flex justify-center space-x-4" data-id="element-1244">
                  <button className="px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 font-medium transition-colors" data-id="element-1245">
                    Download All Files
                  </button>
                  <button onClick={() => navigate('/creative-customizer')} className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1246">
                    Create Another
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </main>
    </div>;
}